import { sigil } from "../shared/plates";
import { withText } from "./png";

/**
 * Saves the story map as a keepsake (map step 4): the map exactly as drawn on screen (its SVG, with each element's
 * computed style written in and its images embedded), on a paper mount like a plate's, with the story's sigil and
 * code. Without names it shows nothing of the story's words, so it's safe to share. The painted ground is AI-made,
 * so the file is marked as a composite with AI-generated media (EU AI Act Art. 50, as on plates).
 */

const STYLE_PROPS = ["fill", "fill-opacity", "stroke", "stroke-width", "stroke-dasharray", "stroke-linecap", "stroke-linejoin", "stroke-opacity", "opacity",
  "font-size", "font-family", "font-weight", "font-style", "letter-spacing", "paint-order", "text-anchor", "display", "filter", "stop-color", "stop-opacity", "mix-blend-mode"] as const;
const SCALE = 3;

const dataUrl = (b: Blob) => new Promise<string>((ok, no) => { const r = new FileReader(); r.onload = () => ok(String(r.result)); r.onerror = () => no(r.error); r.readAsDataURL(b); });
const fmtDate = (t: number) => new Date(t).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });

export async function saveMap(svg: SVGSVGElement, o: { title: string; code: string; names: boolean; painted: boolean; back: string }): Promise<void> {
  const w = svg.viewBox.baseVal.width, h = svg.viewBox.baseVal.height;
  const clone = svg.cloneNode(true) as SVGSVGElement;
  const from = svg.querySelectorAll("*"), to = clone.querySelectorAll("*");
  from.forEach((el, k) => {
    const cs = getComputedStyle(el);
    (to[k] as SVGElement).setAttribute("style", STYLE_PROPS.map((p) => `${p}:${cs.getPropertyValue(p)}`).join(";"));
  });
  // What only the screen needs: hit areas, the selection ring; without names, every word.
  clone.querySelectorAll(".lm-nmap-hit, .lm-nmap-tuft-hit, .lm-nmap-sel").forEach((e) => e.remove());
  if (!o.names) clone.querySelectorAll("text:not(.lm-nmap-rumour text)").forEach((e) => e.remove());
  for (const img of clone.querySelectorAll("image")) {
    const href = img.getAttribute("href");
    if (href && !href.startsWith("data:")) img.setAttribute("href", await dataUrl(await (await fetch(href)).blob()));
  }
  clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  clone.setAttribute("width", String(w * SCALE)); clone.setAttribute("height", String(h * SCALE));
  const pic = new Image();
  pic.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(new XMLSerializer().serializeToString(clone))}`;
  await pic.decode();

  const W = w * SCALE, H = h * SCALE, pad = Math.round(W * 0.05), band = Math.round(W * 0.19);
  const canvas = document.createElement("canvas");
  canvas.width = W + pad * 2; canvas.height = H + pad + band;
  const g = canvas.getContext("2d")!;
  g.fillStyle = "#f2efe8"; g.fillRect(0, 0, canvas.width, canvas.height);
  g.fillStyle = o.back; g.fillRect(pad, pad, W, H);
  g.drawImage(pic, pad, pad, W, H);

  // The sigil and the words of the mount, as on a saved plate.
  const s = sigil(o.code), size = band * 0.56, x0 = pad, y0 = pad + H + (band - size) / 2;
  const at = (q: [number, number]): [number, number] => [x0 + (q[0] / 100) * size, y0 + (q[1] / 100) * size];
  g.strokeStyle = g.fillStyle = "#151515"; g.lineCap = "round";
  g.globalAlpha = 0.45; g.lineWidth = size * 0.03;
  g.beginPath(); g.arc(x0 + size / 2, y0 + size / 2, size * 0.46, 0, Math.PI * 2); g.stroke();
  g.globalAlpha = 1; g.lineWidth = size * 0.05;
  for (const [a, b] of s.chords) { g.beginPath(); g.moveTo(...at(s.points[a]!)); g.lineTo(...at(s.points[b]!)); g.stroke(); }
  g.beginPath(); g.arc(...at(s.points[s.mark]!), size * 0.08, 0, Math.PI * 2); g.fill();
  const tx = x0 + size + pad * 0.6, maxW = canvas.width - tx - pad, unit = W / 1024;
  g.font = `${Math.round(22 * unit)}px ui-monospace, "SF Mono", Menlo, monospace`; g.fillStyle = "#555";
  g.fillText(`WEFTWAKE · MAP · ${o.code}`, tx, y0 + size * 0.22, maxW);
  g.font = `italic ${Math.round(34 * unit)}px Georgia, "Times New Roman", serif`; g.fillStyle = "#151515";
  g.fillText(o.names ? o.title : "A map from Weftwake", tx, y0 + size * 0.58, maxW);
  g.font = `${Math.round(20 * unit)}px ui-monospace, "SF Mono", Menlo, monospace`; g.fillStyle = "#777";
  g.fillText(`${o.painted ? "AI-painted ground · " : ""}drawn by the loom, ${fmtDate(Date.now())}`, tx, y0 + size * 1.02, maxW);

  const blob = await new Promise<Blob>((ok, no) => canvas.toBlob((b) => (b ? ok(b) : no(new Error("toBlob"))), "image/png"));
  const tagged = await withText(blob, {
    Title: `Weftwake map · ${o.code}`,
    ...(o.names ? { Description: o.title } : {}),
    Source: "Weftwake",
    Comment: `weftwake-map:v1:${o.code}`,
    ...(o.painted ? { "Digital Source Type": "http://cv.iptc.org/newscodes/digitalsourcetype/compositeWithTrainedAlgorithmicMedia", Disclaimer: "The ground is an AI-generated image; the map is drawn from the story." } : {}),
  });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(tagged);
  a.download = `weftwake-map-${o.code}.png`;
  document.body.append(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 10_000);
}
